import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import ConsentBanner from './dashboard/components/ConsentBanner';
import reportWebVitals from './reportWebVitals';
import { scrubSentryEvent } from './utils/sentryScrub';
import { installNativeApiBase } from './utils/nativeApiBase';

// In the iPhone app, /api/ calls go to the platform, not the bundled files (utils/nativeApiBase.js). Before any render.
installNativeApiBase();

// ── Sentry error tracking (DEFERRED) ──────────────────────────────────────
// Gated on REACT_APP_SENTRY_DSN so local dev and preview builds don't ship
// errors. Set this in Vercel production env vars only.
//
// Sentry's client + integrations are ~1.8MB of code and were previously
// imported and initialized synchronously at the very top of boot. On iOS 26
// the WKWebView watchdog kills the WebContent process if boot takes too long,
// and parsing/initializing Sentry before first paint was the single biggest
// item on that budget. We now load + init Sentry AFTER first paint (see
// initSentryDeferred below) so it never blocks boot.
//
// To make sure we don't lose anything thrown in the pre-Sentry window, the
// early-error buffer below captures errors synchronously from boot and the
// deferred init flushes them once the client is live.
const sentryDsn = process.env.REACT_APP_SENTRY_DSN;

const __earlyErrors = [];
let __sentry = null; // assigned once the real client finishes loading
// Single capture entry point used app-wide (see window.__bamCapture below).
// Routes to the live Sentry client when loaded, otherwise buffers (with any
// tags/extra context) so nothing thrown before the deferred init is lost.
function queueOrCapture(err, context) {
  if (!err) return;
  if (__sentry) {
    try { __sentry.captureException(err, context); } catch {}
  } else if (__earlyErrors.length < 50) {
    __earlyErrors.push({ err, context });
  }
}
// Expose a stable capture shim so modules (e.g. BAMFull's error boundary)
// can report to Sentry without statically requiring @sentry/react — which
// would pull the whole ~1.8MB client back into the main boot chunk and
// undo the deferral.
if (typeof window !== 'undefined') {
  window.__bamCapture = queueOrCapture;
}
if (sentryDsn && typeof window !== 'undefined') {
  window.addEventListener('error', (e) => queueOrCapture(e?.error || e?.message));
  window.addEventListener('unhandledrejection', (e) => queueOrCapture(e?.reason));
}

// Branded fallback when something throws past every component-level
// boundary. Keeps the user from seeing a blank white screen and gives
// them an obvious "reload" path. The error is routed to Sentry via the
// boundary's componentDidCatch (queued if Sentry hasn't loaded yet).
function GlobalErrorFallback({ resetError }) {
  return (
    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center",
      justifyContent: "center", background: "#0A0A0A",
      fontFamily: "'DM Sans', sans-serif", color: "#F5F5F5",
      padding: 24, textAlign: "center",
    }}>
      <div style={{ maxWidth: 360 }}>
        <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 48,
          color: "#E2DD9F", letterSpacing: 3, lineHeight: 1, marginBottom: 12 }}>
          SOMETHING BROKE
        </div>
        <div style={{ fontSize: 14, color: "rgba(255,255,255,0.65)",
          lineHeight: 1.55, marginBottom: 22 }}>
          The app hit an unexpected error. We've logged it and are on it.
 Try reloading: most of the time that's all it takes.
        </div>
        <button
          onClick={() => { try { resetError && resetError(); } catch {} window.location.reload(); }}
          style={{
            padding: "10px 28px", borderRadius: 8, fontSize: 13, fontWeight: 800,
            background: "#E2DD9F", color: "#111", border: "none", cursor: "pointer",
            fontFamily: "'Bebas Neue', sans-serif", letterSpacing: 1.4,
          }}>
          Reload App
        </button>
      </div>
    </div>
  );
}

// Local replacement for Sentry.ErrorBoundary so the branded fallback works
// without Sentry being loaded at boot. Render errors are forwarded to
// Sentry through queueOrCapture (buffered until the deferred init lands).
class RootErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false }; }
  static getDerivedStateFromError() { return { hasError: true }; }
  componentDidCatch(error) { queueOrCapture(error); }
  resetError = () => this.setState({ hasError: false });
  render() {
    if (this.state.hasError) return <GlobalErrorFallback resetError={this.resetError} />;
    return this.props.children;
  }
}

const root = ReactDOM.createRoot(document.getElementById('root'));
// React.StrictMode intentionally disabled — it double-invokes every
// useEffect on mount in dev, which combined with iOS 26's tighter
// WKWebView watchdog (~200ms before "didBecomeUnresponsive") was
// causing the WebContent process to be killed during boot. Effects
// like supabase.auth.getSession, the community fetch, and the
// subscription check all run 2x, blowing the budget.
//
// We lose strict-mode's hidden-bug detection in dev, but the trade
// is necessary: without this, the app can't even reach the dashboard
// on iOS 26 devices. Re-enable once Capacitor 8.4+ ships with the
// iOS 26 watchdog fix.
root.render(
  <RootErrorBoundary>
    <App />
    <ConsentBanner />
  </RootErrorBoundary>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// ── Capgo OTA: confirm this bundle booted cleanly ─────────────────────────
// Capgo (live JS updates) rolls a freshly-downloaded bundle back to the last
// known-good one if the app never reports it launched (appReadyTimeout). Tell
// it we're up. Native-only and dynamically imported so the web build never
// pulls the plugin. Best-effort — a failure here must never break boot.
if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
  import('@capgo/capacitor-updater')
    .then(({ CapacitorUpdater }) => CapacitorUpdater.notifyAppReady())
    .catch(() => {});
  // The app's own version, for features that switch on only in a reviewed
  // build (featureFlags.js, DM_MIN_NATIVE_VERSION).
  import('./utils/platform').then(m => m.loadNativeAppVersion()).catch(() => {});
  // One light buzz on every tap, wired once (utils/nativeBridge.js installTapHaptics).
  import('./utils/nativeBridge').then(m => m.installTapHaptics()).catch(() => {});
}

// ── Deferred Sentry init ──────────────────────────────────────────────────
// Runs after first paint so the ~1.8MB Sentry bundle never blocks boot. The
// config below is identical to the previous synchronous init; only the
// timing changed. Best-effort — a failed load must never break the app.
function initSentryDeferred() {
  if (!sentryDsn) return;
  import('@sentry/react').then((Sentry) => {
    Sentry.init({
      dsn: sentryDsn,
      environment: process.env.REACT_APP_ENV || "production",
      release: process.env.REACT_APP_COMMIT_SHA || undefined,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          // Coaches type players' names, notes and health details (PQ-08):
          // a replay shows the screen's layout and clicks, never its words.
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,      // don't record Mux videos
        }),
      ],
      tracesSampleRate: 0.1,        // 10% of navigations recorded for perf
      replaysSessionSampleRate: 0,  // no random replays
      replaysOnErrorSampleRate: 1,  // ALWAYS replay when an error occurs
      sendDefaultPii: false,
      beforeSend: scrubSentryEvent, // emails and tokens out; the account id only
      // Filter out noise we already know is harmless
      ignoreErrors: [
        "ResizeObserver loop limit exceeded",
        "Non-Error promise rejection captured",
        "Failed to fetch",             // offline / user navigated away mid-request
        "NetworkError when attempting to fetch resource",
        "Load failed",                 // Safari cancellation idiom
        // SW tombstone teardown noise (b58de08): old cached bundles still run
        // their 30-min reg.update() interval against a SW we just unregistered.
        // Self-resolves as users refresh and pick up the new no-SW bundle.
        "Failed to update a ServiceWorker",
        "Cannot update a null/nonexistent service worker registration",
        // Web Locks API teardown noise. Supabase JS v2 uses navigator.locks
        // for cross-tab session sync. When a user navigates away mid-call
        // (especially in Firefox, which aborts more aggressively), the
        // pending lock request rejects with AbortError. Nothing actually
        // breaks; the next session call just acquires the lock fresh.
        "The lock request is aborted",
        "AbortError: The lock request",
        // Instagram / Meta in-app browser teardown noise. Their internal
        // analytics logger ("navigation_performance_logger_android" /
        // "enableButtonsClickedMetaDataLogging") calls back into the
        // Android Java host on beforeunload; the Java context is already
        // torn down by then so it throws "Java object is gone". This is
        // 100% in Meta's IAB SDK, not in our code. Expect a steady stream
        // of these once the lead-magnet link circulates on IG.
        "Java object is gone",
        "enableButtonsClickedMetaDataLogging",
        "navigation_performance_logger_android",
        // Safari + Supabase Web Locks: when the auth client's lock gets
        // stolen mid-request, Safari can reject the underlying fetch with
        // a bare empty object instead of an AbortError. Sentry surfaces
        // this as "Object captured as promise rejection with keys: [object
        // has no keys]". We catch the obvious offenders at the call site
        // (CertificationPage useEffects, FilmStudio listFilms/getFilm
        // retries) but anything we miss is still benign — the next
        // user-driven action retries naturally. Safety net so the noise
        // doesn't drown out real signal.
        "Object captured as promise rejection",
      ],
    });
    __sentry = Sentry;
    // Publish the live client so modules using window.Sentry (BAMFull
    // auth setUser, AdminErrorBoundary, IntelligencePage) reach the real
    // instance instead of a static require.
    if (typeof window !== 'undefined') window.Sentry = Sentry;
    // Flush anything captured during the pre-Sentry boot window.
    const pending = __earlyErrors.splice(0);
    for (const { err, context } of pending) {
      try { Sentry.captureException(err, context); } catch {}
    }
  }).catch(() => { /* analytics is best-effort; never break the app */ });
}
if (sentryDsn && typeof window !== 'undefined') {
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(initSentryDeferred, { timeout: 4000 });
  } else {
    setTimeout(initSentryDeferred, 2000);
  }
}

// Service worker disabled — the previous cache-first strategy stranded
// returning users on stale JS chunks (blank page). /sw.js still ships as
// a tombstone that unregisters any existing install on next visit.
